import api from 'services/api';
import { REJECT, array, bool, nullable, num, object, optional, str, unknownValue, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

import {
  outputMatcherSchema,
  parameterSchema,
  raiseEventSchema,
  retentionPolicySchema,
  waitEventSchema,
  type OutputMatcher,
  type Parameter,
  type RaiseEvent,
  type RetentionPolicy,
  type SaveNodeDefinition,
  type WaitEvent,
} from './types';

// POST /node_definitions — create. The POST route handler assembles the response record itself (upsert + its own folder-path
// enrichment), so `CreatedNodeDefinition` is redeclared in full rather than shared with the GET-one `NodeDefinition`: they
// are equal-by-coincidence, not identity (see ../../README.md). Structurally identical today, so structural typing lets a
// created record flow wherever a `NodeDefinition` is expected. Field invariants are documented on `getNodeDefinition`.
export interface CreatedNodeDefinition {
  readonly node_definition_id: number;
  readonly version: number;
  // Deliberately stricter than the nullable columns behind them: an unnamed definition is unwritable and unaddressable
  // through this API, so refusing it as unreadable is the wanted outcome. Reasoning in ./getNodeDefinition.ts.
  readonly name: string;
  readonly description: string | null;
  readonly type: string;
  readonly sub_type: string | null;
  readonly documentation: string | null;
  readonly zone_id: number;
  readonly zone_name: string;
  readonly folder_name: string | null;
  readonly default_queue_path: string | null;
  readonly restrict_to_scheduling_window: number | null;
  readonly auto_restart_enabled: boolean;
  readonly auto_restart_amount: number;
  readonly auto_restart_delay_seconds: number;
  readonly restart_on_return_codes: readonly number[];
  readonly hold_on_submit: boolean;
  readonly runtime_limit_min: number | null;
  readonly runtime_limit_max: number | null;
  readonly runtime_limit_min_hhmmss?: string;
  readonly runtime_limit_max_hhmmss?: string;
  readonly runtime_limit_event: number | null;
  readonly runtime_limit_terminate: boolean;
  // Read-only capability flag from node_type_configs; not accepted on write. NULL for a node type whose row leaves it
  // unset (`shell`, `rest`, `sendMail`, `sql`, `tableRead`, `workflowCreate` today) — i.e. the type states no
  // opinion, which consumers read as "not editable".
  readonly editable_handles: boolean | null;
  readonly locks: readonly string[];
  readonly parameters: readonly Parameter[];
  readonly wait_events: readonly WaitEvent[];
  readonly raise_events: readonly RaiseEvent[];
  readonly output_matchers: readonly OutputMatcher[];
  readonly retention_policies: readonly RetentionPolicy[];
  readonly config: unknown;
  readonly handles: readonly unknown[];
  readonly child_nodes: readonly unknown[];
  readonly child_edges: readonly unknown[];
}

const createdSchema = object<CreatedNodeDefinition>({
  node_definition_id: num(REJECT),
  version: num(),
  name: str(REJECT),
  description: nullable(str()),
  type: str(REJECT),
  sub_type: nullable(str()),
  documentation: nullable(str()),
  zone_id: num(),
  zone_name: str(REJECT),
  folder_name: nullable(str()),
  default_queue_path: nullable(str()),
  restrict_to_scheduling_window: nullable(num()),
  auto_restart_enabled: bool(),
  auto_restart_amount: num(),
  auto_restart_delay_seconds: num(),
  restart_on_return_codes: array(num()),
  hold_on_submit: bool(),
  runtime_limit_min: nullable(num()),
  runtime_limit_max: nullable(num()),
  runtime_limit_min_hhmmss: optional(str()),
  runtime_limit_max_hhmmss: optional(str()),
  runtime_limit_event: nullable(num()),
  runtime_limit_terminate: bool(),
  editable_handles: nullable(bool()),
  locks: array(str()),
  parameters: array(parameterSchema),
  wait_events: array(waitEventSchema),
  raise_events: array(raiseEventSchema),
  output_matchers: array(outputMatcherSchema),
  retention_policies: array(retentionPolicySchema),
  config: unknownValue(),
  handles: array(unknownValue()),
  child_nodes: array(unknownValue()),
  child_edges: array(unknownValue()),
});

/**
 * POST `/node_definitions`. Returns the created record.
 * Rejects on HTTP error — the write did not happen, and the server's complaint
 * is the useful thing to report. Answers with the **unreadable** arm when the
 * write DID happen and its receipt cannot be read: by then the server has
 * committed, so that is not a failed save and must not be reported as one
 * (#1035).
 */
export const postNodeDefinition = async (save: SaveNodeDefinition): Promise<Received<CreatedNodeDefinition>> => {
  const res = await api.post('/node_definitions', save);
  return readResponse(res.data, { schema: createdSchema, source: 'postNodeDefinition' });
};
